import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Heart, MapPin, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  type WhenFilter,
  type ContentTypeFilter,
  type ExploreParams,
  WHEN_LABELS,
  CONTENT_TYPE_LABELS,
} from "@/lib/exploreParams";
import { PLACE_TAG_GROUPS, EXCLUDED_TAG_SLUGS } from "@/lib/tags/placeTagGroups";
import type { ExploreMode } from "./ExploreModeToggle";
import type { CityOption } from "@/hooks/useExploreLocation";
import { DALARNA_CITIES } from "@/hooks/useExploreLocation";
import {
  FarmlySheet,
  FarmlySheetBody,
  FarmlySheetFooter,
} from "@/components/ui/FarmlySheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { EXPLORE_DEBUG } from "@/lib/debug";

/* ── Types ── */

interface FilterDraft {
  tags: string[];
  when: WhenFilter;
  contentTypes: ContentTypeFilter[];
  favActive: boolean;
  /** Location action queued (not in draft per se, handled imperatively) */
}

interface ExploreFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ExploreMode;
  params: ExploreParams;
  favActive: boolean;
  onToggleFav: () => void;
  onApply: (updates: Partial<ExploreParams>) => void;
  /** Location state */
  locationActive: boolean;
  locationLabel: string | null;
  onRequestGeo: () => void;
  onSelectCity: (city: CityOption) => void;
  onClearLocation: () => void;
  /** Result count for CTA */
  resultCount: number;
  /** Clear all filters */
  onClearAll: () => void;
}

/* ── Tag groups (memoized once) ── */

const TAG_GROUPS = PLACE_TAG_GROUPS.map((g) => ({
  ...g,
  chips: g.slugs
    .filter((s) => !EXCLUDED_TAG_SLUGS.has(s))
    .map((slug) => ({
      slug,
      label: slug
        .replace(/-/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase()),
    })),
})).filter((g) => g.chips.length > 0);

/* ══════════════════════════════════════════
 * ExploreFilterSheet — Draft + Apply pattern
 * ══════════════════════════════════════════ */

export function ExploreFilterSheet({
  open,
  onOpenChange,
  mode,
  params,
  favActive,
  onToggleFav,
  onApply,
  locationActive,
  locationLabel,
  onRequestGeo,
  onSelectCity,
  onClearLocation,
  resultCount,
  onClearAll,
}: ExploreFilterSheetProps) {
  const isMobile = useIsMobile();

  // ── Draft state: initialized from current params when sheet opens ──
  const [draft, setDraft] = useState<FilterDraft>(() => ({
    tags: params.tags,
    when: params.when,
    contentTypes: params.contentTypes,
    favActive,
  }));

  // Re-init draft when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({
        tags: params.tags,
        when: params.when,
        contentTypes: params.contentTypes,
        favActive,
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft mutators
  const toggleDraftTag = useCallback((slug: string) => {
    setDraft((prev) => {
      const next = prev.tags.includes(slug)
        ? prev.tags.filter((t) => t !== slug)
        : [...prev.tags, slug];
      if (EXPLORE_DEBUG) console.debug("[FilterSheet] toggleTag", slug, "→", next);
      return { ...prev, tags: next };
    });
  }, []);

  const setDraftWhen = useCallback((when: WhenFilter) => {
    setDraft((prev) => ({ ...prev, when }));
  }, []);

  const setDraftContentTypes = useCallback((contentTypes: ContentTypeFilter[]) => {
    setDraft((prev) => ({ ...prev, contentTypes }));
  }, []);

  const toggleDraftFav = useCallback(() => {
    setDraft((prev) => ({ ...prev, favActive: !prev.favActive }));
  }, []);

  // ── Apply: commit draft → global state ──
  const handleApply = useCallback(() => {
    if (EXPLORE_DEBUG) console.debug("[FilterSheet] APPLY", draft);
    // Commit filter params
    onApply({
      tags: draft.tags,
      when: draft.when,
      contentTypes: draft.contentTypes,
    });
    // Commit fav toggle if changed
    if (draft.favActive !== favActive) {
      onToggleFav();
    }
    onOpenChange(false);
  }, [draft, favActive, onApply, onToggleFav, onOpenChange]);

  // ── Clear: reset draft + commit immediately ──
  const handleClear = useCallback(() => {
    if (EXPLORE_DEBUG) console.debug("[FilterSheet] CLEAR");
    onClearAll();
    onOpenChange(false);
  }, [onClearAll, onOpenChange]);

  // ── Count for draft (client-side estimate based on current resultCount + tag delta) ──
  // For now show committed resultCount since client-side draft filtering would require
  // passing full item list. The count updates after apply.
  const draftLabel = mode === "do" ? "aktiviteter" : mode === "farms" ? "gårdar" : "platser";

  const content = (
    <FilterContent
      mode={mode}
      draft={draft}
      onToggleTag={toggleDraftTag}
      onSetWhen={setDraftWhen}
      onSetContentTypes={setDraftContentTypes}
      onToggleFav={toggleDraftFav}
      locationActive={locationActive}
      locationLabel={locationLabel}
      onRequestGeo={onRequestGeo}
      onSelectCity={onSelectCity}
      onClearLocation={onClearLocation}
    />
  );

  // ── Debounced count with micro-animation ──
  const [displayCount, setDisplayCount] = useState(resultCount);
  const [pulse, setPulse] = useState(false);
  const countTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(countTimer.current);
    countTimer.current = setTimeout(() => {
      setDisplayCount((prev) => {
        if (prev !== resultCount) setPulse(true);
        return resultCount;
      });
    }, 200);
    return () => clearTimeout(countTimer.current);
  }, [resultCount]);

  useEffect(() => {
    if (!pulse) return;
    const id = setTimeout(() => setPulse(false), 180);
    return () => clearTimeout(id);
  }, [pulse]);

  const footer = (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={handleClear}
      >
        Rensa
      </Button>
      <Button
        className="flex-1 transition-transform duration-[180ms] ease-out"
        style={{ transform: pulse ? "scale(0.98)" : "scale(1)" }}
        onClick={handleApply}
      >
        Visa {displayCount} {draftLabel}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <FarmlySheet open={open} onOpenChange={onOpenChange} title="Filter" showHeader>
        <FarmlySheetBody>{content}</FarmlySheetBody>
        <FarmlySheetFooter>{footer}</FarmlySheetFooter>
      </FarmlySheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-6 rounded-2xl">
        <DialogHeader>
          <DialogTitle>Filter</DialogTitle>
        </DialogHeader>
        <div className="mt-2">{content}</div>
        <div className="pt-4 border-t border-border mt-4">{footer}</div>
      </DialogContent>
    </Dialog>
  );
}

/* ══════════════════════════════════════════
 * FilterContent — Accordion-based sections
 * ══════════════════════════════════════════ */

interface FilterContentProps {
  mode: ExploreMode;
  draft: FilterDraft;
  onToggleTag: (slug: string) => void;
  onSetWhen: (when: WhenFilter) => void;
  onSetContentTypes: (ct: ContentTypeFilter[]) => void;
  onToggleFav: () => void;
  locationActive: boolean;
  locationLabel: string | null;
  onRequestGeo: () => void;
  onSelectCity: (city: CityOption) => void;
  onClearLocation: () => void;
}

function FilterContent({
  mode,
  draft,
  onToggleTag,
  onSetWhen,
  onSetContentTypes,
  onToggleFav,
  locationActive,
  locationLabel,
  onRequestGeo,
  onSelectCity,
  onClearLocation,
}: FilterContentProps) {
  const draftTagSet = useMemo(() => new Set(draft.tags), [draft.tags]);
  const totalTagsSelected = draft.tags.length;

  // Location summary
  const locationSummary = locationActive
    ? (locationLabel ?? "Nära mig")
    : "Välj stad eller Nära mig";

  return (
    <div className="space-y-4">
      {/* A) Favorites — always visible, no accordion */}
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-2">
          <Heart
            className={cn(
              "h-4 w-4",
              draft.favActive ? "fill-primary text-primary" : "text-muted-foreground"
            )}
          />
          <Label htmlFor="fav-toggle-draft" className="text-sm font-medium">
            Visa bara favoriter
          </Label>
        </div>
        <Switch
          id="fav-toggle-draft"
          checked={draft.favActive}
          onCheckedChange={onToggleFav}
        />
      </div>

      {/* Accordion: max 1 open at a time (type="single" + collapsible) */}
      <Accordion type="single" collapsible className="w-full">
        {/* B) Plats */}
        <AccordionItem value="location" className="border-b-0">
          <AccordionTrigger className="py-3 hover:no-underline">
            <div className="flex items-center gap-2 text-sm font-medium">
              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
              Plats
              <span className="text-xs font-normal text-muted-foreground ml-1">
                — {locationSummary}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pb-2">
              {locationActive ? (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                    <MapPin className="h-3.5 w-3.5" />
                    {locationLabel ?? "Nära mig"} ✓
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={onClearLocation}
                  >
                    Byt
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs gap-1.5"
                    onClick={onRequestGeo}
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    Nära mig
                  </Button>
                  {DALARNA_CITIES.map((city) => (
                    <Button
                      key={city.name}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs"
                      onClick={() => onSelectCity(city)}
                    >
                      {city.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* C) "Vad vill du uppleva?" — places/farms only */}
        {mode !== "do" && (
          <AccordionItem value="tags" className="border-b-0">
            <AccordionTrigger className="py-3 hover:no-underline">
              <div className="flex items-center gap-2 text-sm font-medium">
                Vad vill du uppleva?
                {totalTagsSelected > 0 && (
                  <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
                    {totalTagsSelected}
                  </span>
                )}
                {totalTagsSelected === 0 && (
                  <span className="text-xs font-normal text-muted-foreground ml-1">
                    — 0 valda
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <TagCategoryAccordion
                groups={TAG_GROUPS}
                draftTagSet={draftTagSet}
                onToggleTag={onToggleTag}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* D) "Göra" mode: when + content type */}
        {mode === "do" && (
          <AccordionItem value="when-type" className="border-b-0">
            <AccordionTrigger className="py-3 hover:no-underline">
              <div className="flex items-center gap-2 text-sm font-medium">
                Tid & typ
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  — {WHEN_LABELS[draft.when]}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pb-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">När</p>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(WHEN_LABELS) as WhenFilter[]).map((w) => (
                      <Button
                        key={w}
                        variant={draft.when === w ? "default" : "outline"}
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={() => onSetWhen(w)}
                      >
                        {WHEN_LABELS[w]}
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Typ</p>
                  <div className="space-y-3">
                    {(Object.keys(CONTENT_TYPE_LABELS) as ContentTypeFilter[]).map(
                      (ct) => {
                        const checked = draft.contentTypes.includes(ct);
                        return (
                          <div key={ct} className="flex items-center gap-2">
                            <Checkbox
                              id={`ct-draft-${ct}`}
                              checked={checked}
                              onCheckedChange={(val) => {
                                const next = val
                                  ? [...draft.contentTypes, ct]
                                  : draft.contentTypes.filter((t) => t !== ct);
                                if (next.length > 0) onSetContentTypes(next);
                              }}
                            />
                            <Label htmlFor={`ct-draft-${ct}`} className="text-sm">
                              {CONTENT_TYPE_LABELS[ct]}
                            </Label>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}

/* ══════════════════════════════════════════
 * TagCategoryAccordion — nested per-group accordions
 * ══════════════════════════════════════════ */

interface TagCategoryAccordionProps {
  groups: typeof TAG_GROUPS;
  draftTagSet: Set<string>;
  onToggleTag: (slug: string) => void;
}

function TagCategoryAccordion({ groups, draftTagSet, onToggleTag }: TagCategoryAccordionProps) {
  // Auto-expand first category that has selections, otherwise none
  const defaultValue = useMemo(() => {
    const first = groups.find((g) =>
      g.chips.some((c) => draftTagSet.has(c.slug))
    );
    return first?.key ?? undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

  return (
    <Accordion type="single" collapsible defaultValue={defaultValue} className="w-full pb-2">
      {groups.map((group) => {
        const selectedInGroup = group.chips.filter((c) => draftTagSet.has(c.slug)).length;
        const previewChips = selectedInGroup > 0
          ? group.chips.filter((c) => draftTagSet.has(c.slug)).slice(0, 3).map((c) => c.label).join(", ")
          : "Inga valda";

        return (
          <AccordionItem key={group.key} value={group.key} className="border-b-0">
            <AccordionTrigger className="py-2.5 hover:no-underline text-xs">
              <div className="flex items-center gap-1.5 w-full">
                <span>{group.icon}</span>
                <span className="font-medium">{group.title}</span>
                {selectedInGroup > 0 ? (
                  <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold ml-auto mr-2">
                    {selectedInGroup}
                  </span>
                ) : (
                  <span className="text-muted-foreground font-normal ml-auto mr-2 truncate max-w-[120px]">
                    {previewChips}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-1.5 pb-1">
                {group.chips.map((chip) => (
                  <button
                    key={chip.slug}
                    onClick={() => onToggleTag(chip.slug)}
                    className={cn(
                      "px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                      draftTagSet.has(chip.slug)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-accent"
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
